import { useMemo } from 'react';

import { endpoints } from '@/config/apiEndpoints';
import { useActiveOrganizationId } from '@/features/auth/session';
import { byName } from '@/features/brandHub/byName';
import { brandHubQueryKey } from '@/features/brandHub/queryKeys';
import { useLiveCollection, type LiveCollection } from '@/hooks/useLiveCollection';
import { collections } from '@/lib/firebase';
import { parseBrand, type Brand } from './types';

const BRAND_LIMIT = 200;

/** Every active brand of the organization. */
export function useBrands(): LiveCollection<Brand> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<Brand>({
    path: collections.brands(organizationId ?? '_'),
    httpPath: endpoints.brandHub.brands(),
    queryKey: brandHubQueryKey('brands', organizationId),
    parse: parseBrand,
    limit: BRAND_LIMIT,
    enabled: Boolean(organizationId),
  });

  const data = useMemo(
    () => byName(
      collection.data.filter((brand) => brand.isActive),
      (brand) => brand.name,
    ),
    [collection.data],
  );
  return { ...collection, data };
}

/** One brand by slug, off the same stream the list reads. */
export function useBrand(slug: string) {
  const brands = useBrands();
  return { ...brands, brand: brands.data.find((brand) => brand.id === slug) };
}

/** Every tag in use, for a filter to offer. */
export function useAvailableTags(rows: { tags: string[] }[]): string[] {
  return useMemo(
    () => [...new Set(rows.flatMap((row) => row.tags))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );
}

/** The rows whose name contains the query and that carry a selected tag. */
export function useFilteredByNameAndTags<T extends { name: string; tags: string[] }>(
  rows: T[],
  query: string,
  tags: readonly string[],
): T[] {
  return useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (!needle || row.name.toLowerCase().includes(needle)) &&
        (tags.length === 0 || row.tags.some((tag) => tags.includes(tag))),
    );
  }, [rows, query, tags]);
}
